import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatMonthShort } from "../../lib/format";
import type { VacancyAnnotation, VacancyReason } from "../../lib/realestate";
import { Modal } from "../ui/Modal";

const REASON_ORDER: VacancyReason[] = [
  "MIETERWECHSEL",
  "RENOVIERUNG",
  "KEINE_NACHFRAGE",
  "EIGENNUTZUNG",
  "SONSTIGES",
];

interface VacancyAnnotationFormModalProps {
  open: boolean;
  onClose: () => void;
  unitLabel: string;
  startMonth: string;
  existing: VacancyAnnotation | null;
  onSubmit: (data: { reason: VacancyReason; notes: string | null }) => Promise<void>;
  submitting: boolean;
}

export function VacancyAnnotationFormModal({
  open,
  onClose,
  unitLabel,
  startMonth,
  existing,
  onSubmit,
  submitting,
}: VacancyAnnotationFormModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<VacancyReason | "">(existing?.reason ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const title = existing
    ? t("realestate.vacancy.form_title_edit")
    : t("realestate.vacancy.form_title_create");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!reason) {
      setError(t("realestate.vacancy.error_reason_required"));
      return;
    }
    if (notes.length > 500) {
      setError(t("realestate.vacancy.error_notes_too_long"));
      return;
    }
    await onSubmit({ reason, notes: notes.trim() === "" ? null : notes.trim() });
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="text-sm text-slate-600">
          {unitLabel} · {formatMonthShort(startMonth)}
        </div>

        <label className="block">
          <span className="block text-sm font-medium mb-1">
            {t("realestate.vacancy.reason_label")} *
          </span>
          <select
            className="w-full border rounded px-2 py-1"
            value={reason}
            onChange={(e) => setReason(e.target.value as VacancyReason | "")}
            required
          >
            <option value="">--</option>
            {REASON_ORDER.map((r) => (
              <option key={r} value={r}>
                {t(`realestate.vacancy.reason_${r}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-sm font-medium mb-1">
            {t("realestate.vacancy.notes_label")}
          </span>
          <textarea
            className="w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("realestate.vacancy.notes_placeholder")}
            maxLength={500}
            rows={3}
          />
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1 border rounded"
            onClick={onClose}
            disabled={submitting}
          >
            {t("realestate.vacancy.form_cancel")}
          </button>
          <button
            type="submit"
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={submitting}
          >
            {t("realestate.vacancy.form_save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
